import React, { useState, useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation
} from "react-router-dom";
import "antd/dist/reset.css";
import { ApolloProvider } from "@apollo/client";
import { unSetUserToken } from "./features/authSlice";
import { Layout } from "antd";
import Leads from "./Home/Leads"
import Sidebar from "./Sidebar/Sidebar";
import LeadAssign from "./Assign/leadAssign";
import Dashboard from "./Home/Dashboard";
import LoginP from "./Components/LoginPage";
import Header from "./reusableField/header";
import Fls from "./Home/fls";
import { createApolloClient, createErrorLink } from "./services/apolloClient";
import LeadDetails from "./Components/LeadDetails";
import FlsDetails from "./Components/FlsDetails";
import ClientPage from "./Components/clientpage";
import UserDashboard from "./Components/user/userDashboard"
import ResearchSection from "./Components/researchSection";
import Companies from "./Components/companies/companies";
import FileUpload from "./Home/fileupload";
import ForgotPassword from "./Components/forgotPassword";
import { RoleProvider } from "./reusableField/roleContext";

// Create a context for authentication
const AuthContext = React.createContext();

// Global fetch interceptor
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  try {
    const response = await originalFetch(...args);
    if (response.status === 401) {
      localStorage.removeItem("accessToken");
      // Dispatch custom event instead of using window.location.href
      window.dispatchEvent(new CustomEvent('tokenExpired'));
      return Promise.reject(new Error("Unauthorized"));
    }
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

// Create a separate component for the app logic that uses Router hooks
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle token expiration
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("accessToken");
    sessionStorage.removeItem("dashboardRoleId");
    sessionStorage.removeItem("accountId");
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("leads_filter_state");
    navigate("/login");
  };

  // Create Apollo client only once using useMemo
  const client = useMemo(() => {
    const errorLink = createErrorLink(handleLogout);
    return createApolloClient(errorLink);
  }, []); // Empty deps - create only once

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpired = () => {
      handleLogout();
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    return () => window.removeEventListener('tokenExpired', handleTokenExpired);
  }, []);

  // Initialize authentication state only once
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      if (!location.pathname.includes("login") && !location.pathname.includes("forgot")) {
        navigate("/login", { state: { from: location.pathname } });
      }
    }

    setLoading(false);
  }, []); // Run only once on mount

  if (loading) {
    return <div></div>; // Show nothing while loading
  }

  return (
    <ApolloProvider client={client}>
      <RoleProvider roleId={roleId}>
        {isAuthenticated ? (
          <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <div style={{ flex: 1 }}>
              <Header setIsAuthenticated={setIsAuthenticated} />
              <Routes>

                <Route path="/leads" element={<Leads />} />
                <Route path="/assign" element={<LeadAssign />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/fls" element={<Fls />} />
                <Route path="/leads/leadprofile" element={<LeadDetails />} />
                <Route path="/fls/flsprofile" element={<FlsDetails />} />
                <Route path="/client" element={<ClientPage />} />
                <Route path="/userdashboard" element={<UserDashboard />} />
                <Route path="/research" element={<ResearchSection />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/fileupload" element={<FileUpload />} />

              </Routes>
            </div>
          </Layout>
        ) : (
          <Routes>
            <Route
              path="/login"
              element={<LoginP setIsAuthenticated={setIsAuthenticated} />}
            />
            <Route path="*" element={<Navigate to="/login" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Routes>
        )}
      </RoleProvider>
    </ApolloProvider>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;